import { SharedModals, TransactionsModals } from "@/constants/enums";
import { useWeb3 } from "@/context/Web3Context";
import { useBlockchainData, useCompanyData } from "@/hooks";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import DepositBankDetailsComponent from "./DepositBankDetailsComponent";
import WithdrawalTransactionComponent from "./WithdrawalTransactionComponent";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FramerButton } from "@/components/shared";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { DialogTitle } from "@radix-ui/react-dialog";

type Props = {
  amount: number;
  showModal: TransactionsModals | SharedModals;
  setAmount: Dispatch<SetStateAction<number>>;
  setShowModal: Dispatch<SetStateAction<TransactionsModals>>;
  referencenumber: string;
};

function AddTransactionModal(props: Props) {
  // Props
  const {
    amount,
    setAmount,
    setShowModal,
    referencenumber,
    showModal,
  } = props;

  // States
  const [inputErrorText, setInputErrorText] = useState("");
  const [currentTab, setCurrentTab] = useState("deposit");

  // Hooks
  const company = useCompanyData();
  const { t } = useTranslation(["modals"]);
  const blockchainData = useBlockchainData();

  // Magic SDK
  const {
    isConnected,
  } = useWeb3();

  // Constants
  const bankAccountIsVerified = company?.bankaccount?.verified;
  const walletConnectedAndBankVerified = isConnected && bankAccountIsVerified;
  const notConnectedAndBankVerified = !isConnected && bankAccountIsVerified;

  const handleClose = () => {
    setShowModal(TransactionsModals.NoModal);
    setAmount(0);
    setInputErrorText("");
  };

  const tabTriggerClasses = "data-[state=active]:bg-mfpurple data-[state=active]:text-white data-[state=active]:dark:bg-mforange";

  return (
    <Dialog open={showModal === TransactionsModals.AddTransaction} onOpenChange={() => setShowModal(TransactionsModals.NoModal)}>
      <DialogContent className="px-6 max-w-5xl min-w-4xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="sr-only">
            Add Transaction
          </DialogTitle>
        </DialogHeader>
        {/* Main Content Container*/}
        <div className="flex-1 pt-2 overflow-y-auto">
          <Tabs defaultValue="deposit" onValueChange={value => setCurrentTab(value)}>
            <div className="flex justify-center">
              <TabsList>
                <TabsTrigger value="deposit" className={tabTriggerClasses}>{t("addtransaction.buttons.deposit")}</TabsTrigger>
                <TabsTrigger value="withdraw" className={tabTriggerClasses}>{t("addtransaction.buttons.withdraw")}</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="deposit">
              <DepositBankDetailsComponent referencenumber={referencenumber} />
            </TabsContent>
            <TabsContent value="withdraw">
              <WithdrawalTransactionComponent
                amount={amount}
                setAmount={setAmount}
                inputErrorText={inputErrorText}
                setInputErrorText={setInputErrorText}
                bankAccountIsVerified={bankAccountIsVerified}
                walletConnectedAndBankVerified={walletConnectedAndBankVerified}
                notConnectedAndBankVerified={notConnectedAndBankVerified}
                balance={blockchainData.balance}
              />
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className="pt-2">
          <div className="flex items-center justify-between mt-4 w-full">
            <FramerButton
              onClick={handleClose}
              variant="outline-primary"
            >
              {t("common:buttons.cancel")}
            </FramerButton>
            {
              walletConnectedAndBankVerified && currentTab !== "deposit" &&
              <FramerButton
                onClick={() => {
                  setShowModal(TransactionsModals.WithdrawalSummary);
                }}
                className={`font-semibold whitespace-nowrap text-white
                  ${blockchainData.balance <= 0 ? "bg-gray-500 dark:bg-gray-700 cursor-not-allowed" : "bg-mforange hover:bg-mforange/80"}`}
                disabled={blockchainData.balance <= 0 || inputErrorText.length > 0}
              >
                {t("common:buttons.continue")}
              </FramerButton>
            }
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddTransactionModal;
